import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Download } from "lucide-react";
import { exportToCsv } from "@/lib/exportCsv";

const CLASSES = ["CMT4", "CMT5", "CMT6", "PST4", "PST5", "PST6", "SW4", "SW5", "SW6"];
const SEMESTERS = ["I", "II"];
const THRESHOLD = 90;

export function AttendanceEligibilityTab() {
  const currentYear = new Date().getFullYear();
  const [academicYear, setAcademicYear] = useState<string>(`${currentYear}/${currentYear + 1}`);
  const [semester, setSemester] = useState<string>("");
  const [klass, setKlass] = useState<string>("");

  const enabled = !!semester && !!klass;

  const { data: students = [] } = useQuery({
    queryKey: ["elig-students", klass],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("students")
        .select("id, first_name, last_name, student_number")
        .eq("class", klass)
        .in("status", ["registered", "active", "continuing"])
        .order("first_name");
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: moduleSessions = [] } = useQuery({
    queryKey: ["elig-module-sessions", semester, klass],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("module_sessions")
        .select("id, module_code")
        .eq("semester", semester)
        .eq("class", klass)
        .eq("active", true);
      if (error) throw error;
      return data ?? [];
    },
  });

  const moduleSessionIds = useMemo(() => moduleSessions.map((m: any) => m.id), [moduleSessions]);

  const { data: attendanceSessions = [] } = useQuery({
    queryKey: ["elig-att-sessions", moduleSessionIds.join(",")],
    enabled: enabled && moduleSessionIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("attendance_sessions")
        .select("id, module_session_id")
        .in("module_session_id", moduleSessionIds)
        .eq("status", "closed")
        .eq("excluded_from_coverage", false);
      if (error) throw error;
      return data ?? [];
    },
  });

  const attSessionIds = useMemo(() => attendanceSessions.map((s: any) => s.id), [attendanceSessions]);
  const studentIds = useMemo(() => students.map((s: any) => s.id), [students]);

  const { data: presentRecs = [], isFetching: loadingRecs } = useQuery({
    queryKey: ["elig-records", attSessionIds.join(","), studentIds.join(",")],
    enabled: enabled && attSessionIds.length > 0 && studentIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("attendance_records")
        .select("student_id, session_id")
        .eq("attendance_type", "session")
        .eq("status", "present")
        .in("session_id", attSessionIds)
        .in("student_id", studentIds);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { modules, sessionsByModule, presentByStudentModule } = useMemo(() => {
    const modCodes = Array.from(new Set(moduleSessions.map((m: any) => m.module_code))).sort();
    const msIdToCode = new Map<string, string>(moduleSessions.map((m: any) => [m.id, m.module_code]));
    const attIdToCode = new Map<string, string>();
    const sessionsByMod = new Map<string, number>();
    attendanceSessions.forEach((s: any) => {
      const code = msIdToCode.get(s.module_session_id);
      if (!code) return;
      attIdToCode.set(s.id, code);
      sessionsByMod.set(code, (sessionsByMod.get(code) ?? 0) + 1);
    });
    const presence = new Map<string, Map<string, Set<string>>>(); // student -> module -> set(session)
    presentRecs.forEach((r: any) => {
      const code = attIdToCode.get(r.session_id);
      if (!code) return;
      if (!presence.has(r.student_id)) presence.set(r.student_id, new Map());
      const mmap = presence.get(r.student_id)!;
      if (!mmap.has(code)) mmap.set(code, new Set());
      mmap.get(code)!.add(r.session_id);
    });
    return { modules: modCodes, sessionsByModule: sessionsByMod, presentByStudentModule: presence };
  }, [moduleSessions, attendanceSessions, presentRecs]);

  const handleExport = () => {
    const headers = ["SN", "Student Name", "Reg No", ...modules];
    const rows = students.map((s: any, idx: number) => {
      const cells = [
        String(idx + 1),
        `${s.first_name ?? ""} ${s.last_name ?? ""}`.trim(),
        s.student_number ?? "",
      ];
      modules.forEach(code => {
        const scanned = sessionsByModule.get(code) ?? 0;
        const present = presentByStudentModule.get(s.id)?.get(code)?.size ?? 0;
        const pct = scanned > 0 ? Math.round((present / scanned) * 100) : null;
        cells.push(pct === null ? "—" : `${pct >= THRESHOLD ? "Eligible" : "Not Eligible"} (${present}/${scanned} ${pct}%)`);
      });
      return cells;
    });
    exportToCsv(`eligibility-${klass}-sem${semester}-${academicYear.replace("/", "-")}.csv`, headers, rows);
  };

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader>
          <CardTitle>Attendance Eligibility</CardTitle>
          <CardDescription>Students with ≥{THRESHOLD}% session attendance per module are eligible.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div>
              <Label>Academic Year</Label>
              <Input value={academicYear} onChange={e => setAcademicYear(e.target.value)} placeholder="2025/2026" />
            </div>
            <div>
              <Label>Semester</Label>
              <Select value={semester} onValueChange={setSemester}>
                <SelectTrigger><SelectValue placeholder="Select semester" /></SelectTrigger>
                <SelectContent position="popper">
                  {SEMESTERS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Class</Label>
              <Select value={klass} onValueChange={setKlass}>
                <SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
                <SelectContent position="popper">
                  {CLASSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" size="sm" onClick={handleExport} disabled={!enabled || students.length === 0}>
                <Download className="h-4 w-4 mr-1" /> Export CSV
              </Button>
            </div>
          </div>

          {enabled && (
            <div className="table-responsive">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">SN</TableHead>
                    <TableHead>Student Name</TableHead>
                    <TableHead>Reg No</TableHead>
                    {modules.map(code => (
                      <TableHead key={code} className="text-center font-mono">{code}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {students.map((s: any, idx: number) => (
                    <TableRow key={s.id}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell className="whitespace-nowrap">{s.first_name} {s.last_name}</TableCell>
                      <TableCell className="font-mono text-xs">{s.student_number ?? "—"}</TableCell>
                      {modules.map(code => {
                        const scanned = sessionsByModule.get(code) ?? 0;
                        const present = presentByStudentModule.get(s.id)?.get(code)?.size ?? 0;
                        const pct = scanned > 0 ? Math.round((present / scanned) * 100) : null;
                        return (
                          <TableCell key={code} className="text-center">
                            {pct === null ? (
                              <span className="text-muted-foreground">—</span>
                            ) : (
                              <div className="flex flex-col items-center gap-0.5">
                                <Badge variant={pct >= THRESHOLD ? "default" : "destructive"}>
                                  {pct >= THRESHOLD ? "Eligible" : "Not Eligible"}
                                </Badge>
                                <span className="text-[10px] text-muted-foreground">{present}/{scanned} ({pct}%)</span>
                              </div>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                  {students.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3 + modules.length} className="text-center text-muted-foreground py-8">
                        {loadingRecs ? "Loading..." : "No registered students for this class."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
